import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { response } from '../../../api/db';
import { resetPassword } from '../../../api/db/user';
import {
  gray100,
  primaryButtonBackgroundColor
} from '../../../constants/Colors';
import { headerHeight, spHeaderHeight } from '../../../constants/Location';
import { common } from '../../../modules/error';
import { userPasswordResetInputSchema } from '../../../validation/user';
import type { UserPasswordResetInputType } from '../../../validation/user/userType';

import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import Header from '../../uiParts/Header';
import PasswordTextfield from '../../uiParts/PasswordTextfield';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  button: {
    height: '44px',
    fontSize: '16px',
    fontWeight: '500'
  },
  container: {
    '@media screen and (max-width:600px)': {
      height: `calc( 100vh - ${spHeaderHeight} - 24px)`,
      m: `${spHeaderHeight} auto 0`,
      pt: '24px',
      width: '90%'
    },
    height: `calc( 100vh - ${headerHeight} - 56px )`,
    m: `${headerHeight} auto 0`,
    pt: '56px',
    width: '560px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    mb: '24px'
  }
};

const AuthPasswordResetInput: React.FC = () => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const token = params.get('token');
  const navigate = useNavigate();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<UserPasswordResetInputType>({
    resolver: yupResolver(userPasswordResetInputSchema)
  });

  const buttonDisabledFlag = (): boolean => {
    return (
      watch('password') === undefined ||
      '' ||
      watch('passwordConfirm') === undefined ||
      '' ||
      Boolean(errors.password) ||
      Boolean(errors.passwordConfirm)
    );
  };

  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      if (token === null) {
        throw new Error('token is null');
      }
      await resetPassword(watch('password'), token);
      navigate('/auth/password_reset/complete');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomHead title="パスワード忘れ | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Header />
      <Box component="div" sx={styles.container}>
        <Typography sx={styles.title}>パスワード再設定</Typography>
        <form>
          <Controller
            render={({ field }) => (
              <PasswordTextfield
                {...field}
                error={Boolean(errors.password)}
                formMb="24px"
                helperText={errors.password?.message}
                labelText="新しいパスワード"
                requiredText="必須"
              />
            )}
            control={control}
            name="password"
          />
          <Controller
            render={({ field }) => (
              <PasswordTextfield
                {...field}
                error={Boolean(errors.passwordConfirm)}
                formMb="40px"
                helperText={errors.passwordConfirm?.message}
                labelText="新しいパスワード(確認用)"
                requiredText="必須"
              />
            )}
            control={control}
            name="passwordConfirm"
          />
          <Button
            disabled={buttonDisabledFlag()}
            fullWidth
            onClick={() => {
              void (async () => {
                await handleSubmit(async () => await onSubmit())();
              })();
            }}
            sx={[primaryButtonBackgroundColor, styles.button]}
            variant="contained"
          >
            パスワードを再設定する
          </Button>
        </form>
      </Box>
      <CustomSnackbar
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        open={open}
        text={errorText}
      />
    </>
  );
};

export default AuthPasswordResetInput;
